/**
 * Generic CRUD manager — provides table + add/edit modal with configurable fields.
 */
import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Plus, Pencil, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";

const FileInput = ({ onUploaded, label }) => {
  const [loading, setLoading] = useState(false);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const form = new FormData(); form.append("file", file);
    setLoading(true);
    try {
      const { data } = await api.post("/upload", form, { headers: { "Content-Type": "multipart/form-data" } });
      onUploaded(data.url);
      toast.success("Berhasil diunggah");
    } catch { toast.error("Gagal mengunggah"); } finally { setLoading(false); }
  };
  return (
    <label className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-300 cursor-pointer text-xs bg-white hover:bg-slate-50">
      <Upload className="w-3.5 h-3.5" /> {loading ? "Mengunggah..." : (label || "Unggah File")}
      <input type="file" className="hidden" onChange={upload} accept="image/*,.pdf,.doc,.docx" />
    </label>
  );
};

export default function CRUDPage({ title, endpoint, fields, columns, defaults = {}, listPath = "" }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(defaults);
  const [loading, setLoading] = useState(false);

  const path = `/${endpoint}`;
  const list = async () => {
    try {
      const { data } = await api.get(`${path}?limit=200`);
      setItems(data.items);
    } catch { toast.error("Gagal memuat data"); }
  };
  useEffect(() => { list(); }, []);

  const openCreate = () => { setEditing(null); setForm(defaults); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...defaults, ...it }); setOpen(true); };

  const save = async () => {
    setLoading(true);
    try {
      // Build payload only with declared fields to avoid sending extra ones
      const payload = {};
      fields.forEach((f) => {
        let v = form[f.name];
        if (f.type === "number") v = v === "" || v == null ? null : Number(v);
        if (f.type === "boolean") v = Boolean(v);
        payload[f.name] = v;
      });
      if (editing) await api.put(`${path}/${editing.id}`, payload);
      else await api.post(path, payload);
      toast.success(editing ? "Berhasil diperbarui" : "Berhasil ditambahkan");
      setOpen(false);
      list();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan"); } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Hapus data ini?")) return;
    try { await api.delete(`${path}/${id}`); toast.success("Berhasil dihapus"); list(); } catch { toast.error("Gagal menghapus"); }
  };

  const renderField = (f) => {
    const val = form[f.name] ?? "";
    const set = (v) => setForm({ ...form, [f.name]: v });
    if (f.type === "textarea") return <Textarea data-testid={`crud-field-${f.name}`} rows={f.rows || 4} value={val} onChange={(e) => set(e.target.value)} />;
    if (f.type === "select") return (
      <Select value={val || ""} onValueChange={set}>
        <SelectTrigger data-testid={`crud-field-${f.name}`}><SelectValue placeholder="Pilih..." /></SelectTrigger>
        <SelectContent>{f.options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}</SelectContent>
      </Select>
    );
    if (f.type === "file") return (
      <div className="flex items-center gap-3">
        <Input data-testid={`crud-field-${f.name}`} value={val} onChange={(e) => set(e.target.value)} placeholder="URL atau unggah" className="flex-1" />
        <FileInput onUploaded={set} />
      </div>
    );
    if (f.type === "boolean") return (
      <label className="inline-flex items-center gap-2 cursor-pointer">
        <input type="checkbox" data-testid={`crud-field-${f.name}`} checked={!!val} onChange={(e) => set(e.target.checked)} />
        <span className="text-sm">Ya</span>
      </label>
    );
    return <Input data-testid={`crud-field-${f.name}`} type={f.type || "text"} value={val} onChange={(e) => set(e.target.value)} />;
  };

  return (
    <div data-testid={`admin-crud-${endpoint}`}>
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-serif text-3xl font-bold text-[#0B1D3A]">{title}</h1>
        <Button data-testid="crud-add-btn" onClick={openCreate} className="bg-amber-500 hover:bg-amber-600 text-[#0B1D3A] font-bold"><Plus className="w-4 h-4 mr-1" /> Tambah</Button>
      </div>

      <div className="card-elevated overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-500">
            <tr>
              {columns.map((c) => <th key={c.key} className="px-5 py-3 font-semibold">{c.label}</th>)}
              <th className="px-5 py-3 w-32 text-right">Aksi</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it) => (
              <tr key={it.id} data-testid={`crud-row-${it.id}`} className="border-t border-slate-100 hover:bg-slate-50">
                {columns.map((c) => (
                  <td key={c.key} className="px-5 py-3">
                    {c.render ? c.render(it) : <span className="text-slate-700">{String(it[c.key] ?? "-").slice(0, 80)}</span>}
                  </td>
                ))}
                <td className="px-5 py-3 text-right">
                  <button data-testid={`crud-edit-${it.id}`} onClick={() => openEdit(it)} className="w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-amber-50 text-amber-600"><Pencil className="w-4 h-4" /></button>
                  <button data-testid={`crud-delete-${it.id}`} onClick={() => remove(it.id)} className="ml-1 w-8 h-8 inline-flex items-center justify-center rounded-lg hover:bg-red-50 text-red-600"><Trash2 className="w-4 h-4" /></button>
                </td>
              </tr>
            ))}
            {items.length === 0 && <tr><td colSpan={columns.length + 1} className="px-5 py-10 text-center text-slate-500">Belum ada data.</td></tr>}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? `Edit ${title}` : `Tambah ${title}`}</DialogTitle></DialogHeader>
          <div className="grid gap-4 py-2">
            {fields.map((f) => (
              <div key={f.name} className={f.full ? "col-span-2" : ""}>
                <Label className="text-xs font-semibold">{f.label}</Label>
                <div className="mt-1">{renderField(f)}</div>
              </div>
            ))}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Batal</Button>
            <Button data-testid="crud-save-btn" onClick={save} disabled={loading} className="bg-[#0B1D3A] hover:bg-[#132D58] text-white">{loading ? "Menyimpan..." : "Simpan"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
